import React, { useState, useEffect } from 'react';
import { X, Fingerprint, Shield, Check, AlertCircle, Trash2, Smartphone, Loader2 } from 'lucide-react';
import { 
    isBiometricAvailable, 
    registerBiometric, 
    getBiometricType,
    getBiometricCredentials,
    removeBiometricCredential,
    BiometricCredential,
    isBiometricSupportedPlatform
} from '../utils/biometricAuth';
import { User } from '../types';

interface BiometricSetupModalProps {
    isOpen: boolean;
    onClose: () => void;
    user: User;
    isDarkMode: boolean;
    showToast: (type: 'success' | 'error' | 'warning' | 'info', message: string) => void;
}

const BiometricSetupModal: React.FC<BiometricSetupModalProps> = ({
    isOpen,
    onClose,
    user,
    isDarkMode,
    showToast
}) => {
    const [isAvailable, setIsAvailable] = useState(false);
    const [isChecking, setIsChecking] = useState(true);
    const [isRegistering, setIsRegistering] = useState(false);
    const [credentials, setCredentials] = useState<BiometricCredential[]>([]);
    const [biometricType, setBiometricType] = useState('Biometric Authentication');
    const [platformSupported, setPlatformSupported] = useState(true);
    const [lastError, setLastError] = useState<string | null>(null);
    const [retryCount, setRetryCount] = useState(0);

    useEffect(() => {
        if (isOpen) {
            checkAvailability();
            loadCredentials();
        }
    }, [isOpen, user?.id]);

    const checkAvailability = async () => {
        setIsChecking(true);
        // Check platform support first (APK and Windows only)
        const supported = await isBiometricSupportedPlatform();
        setPlatformSupported(supported);
        
        if (supported) {
            const available = await isBiometricAvailable();
            setIsAvailable(available);
            setBiometricType(getBiometricType());
        } else {
            setIsAvailable(false);
        }
        setIsChecking(false);
    };

    const loadCredentials = () => {
        if (user?.id) {
            const creds = getBiometricCredentials(user.id);
            setCredentials(creds);
        }
    };

    const handleSetup = async (isRetry = false) => {
        if (!user) return;

        if (isRetry) {
            setRetryCount(prev => prev + 1);
        }

        setIsRegistering(true);
        setLastError(null);
        
        try {
            const result = await registerBiometric(
                user.id,
                user.email || user.username,
                user.username
            );

            if (result.success) {
                showToast('success', '✅ Biometric authentication set up successfully!');
                setRetryCount(0);
                setLastError(null);
                loadCredentials();
            } else {
                const errorMsg = result.error || 'Failed to set up biometric authentication.';
                setLastError(errorMsg);
                showToast('error', errorMsg);
            }
        } catch (error) {
            console.error('[BiometricSetup] Error:', error);
            const errorMsg = 'An error occurred while setting up biometric authentication.';
            setLastError(errorMsg);
            showToast('error', errorMsg);
        } finally {
            setIsRegistering(false);
        }
    };

    const handleRemove = (credentialId: string) => {
        if (!user) return;

        const success = removeBiometricCredential(user.id, credentialId);
        if (success) {
            showToast('success', 'Biometric credential removed.');
            loadCredentials();
        } else {
            showToast('error', 'Failed to remove credential.');
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in duration-300" style={{ alignItems: 'center' }}>
            <div 
                className={`relative w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 my-auto ${
                    isDarkMode ? 'bg-slate-900 border border-slate-700' : 'bg-white border border-slate-200'
                }`}
                style={{ maxHeight: '90vh', overflow: 'auto' }}
            >
                {/* Close Button */}
                <button
                    onClick={onClose}
                    className={`absolute top-4 right-4 p-2 rounded-lg transition-all duration-200 z-10 ${
                        isDarkMode 
                            ? 'hover:bg-slate-800 text-slate-400 hover:text-white' 
                            : 'hover:bg-slate-100 text-slate-500 hover:text-slate-900'
                    }`}
                >
                    <X size={20} />
                </button>

                {/* Header */}
                <div className={`relative overflow-hidden p-8 pb-6 ${
                    isDarkMode ? 'bg-gradient-to-br from-purple-900/20 to-indigo-900/20' : 'bg-gradient-to-br from-purple-50 to-indigo-50'
                }`}>
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-48 h-48 bg-purple-500/10 rounded-full blur-3xl" />
                    
                    <div className="relative flex flex-col items-center text-center">
                        <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center shadow-lg shadow-purple-500/50 mb-4">
                            <Fingerprint size={36} className="text-white" />
                        </div>

                        <h2 className={`text-2xl font-black mb-2 ${
                            isDarkMode ? 'text-white' : 'text-slate-900'
                        }`}>
                            Biometric Authentication
                        </h2>
                        
                        <p className={`text-sm leading-relaxed max-w-xs ${
                            isDarkMode ? 'text-slate-400' : 'text-slate-600'
                        }`}>
                            Secure your account with {biometricType.toLowerCase()} for quick and easy access.
                        </p>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6 space-y-4">
                    {isChecking ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 size={32} className="animate-spin text-purple-500" />
                        </div>
                    ) : !platformSupported ? (
                        <div className={`p-4 rounded-xl border ${
                            isDarkMode ? 'bg-blue-950/50 border-blue-900/50' : 'bg-blue-50 border-blue-200'
                        }`}>
                            <div className="flex items-start gap-3">
                                <Smartphone size={20} className="text-blue-500 flex-shrink-0 mt-0.5" />
                                <div>
                                    <p className={`text-sm font-semibold mb-1 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                                        Platform Not Supported
                                    </p>
                                    <p className={`text-xs leading-relaxed ${
                                        isDarkMode ? 'text-blue-300' : 'text-blue-700'
                                    }`}>
                                        Your browser or device doesn't support WebAuthn biometric authentication. Try:
                                    </p>
                                    <ul className={`text-xs mt-2 space-y-1 ${
                                        isDarkMode ? 'text-blue-300' : 'text-blue-700'
                                    }`}>
                                        <li>• Use a modern browser (Chrome, Edge, Safari, Firefox)</li>
                                        <li>• Ensure you're on HTTPS (not HTTP)</li>
                                        <li>• Download the Android APK for mobile biometrics</li>
                                        <li>• Use Windows with Windows Hello enabled</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Warning if not available */}
                            {!isAvailable && (
                                <div className={`p-4 rounded-xl border ${
                                    isDarkMode ? 'bg-amber-950/50 border-amber-900/50' : 'bg-amber-50 border-amber-200'
                                }`}>
                                    <div className="flex items-start gap-3">
                                        <AlertCircle size={20} className="text-amber-500 flex-shrink-0 mt-0.5" />
                                        <div>
                                            <p className="text-sm font-semibold text-amber-500 mb-1">Setup Required</p>
                                            <p className={`text-xs leading-relaxed ${
                                                isDarkMode ? 'text-amber-400' : 'text-amber-700'
                                            }`}>
                                                Biometric hardware detected but not configured. 
                                                Click the button below to set up {biometricType}.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                            
                            
                            {/* Benefits */}
                            <div className={`p-4 rounded-xl border ${
                                isDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-purple-50/50 border-purple-200/50'
                            }`}>
                                <h3 className={`text-xs font-bold uppercase tracking-wider mb-3 ${
                                    isDarkMode ? 'text-purple-400' : 'text-purple-600'
                                }`}>Benefits</h3>
                                <div className="space-y-2">
                                    {[
                                        'Instant login without passwords',
                                        'Enhanced security with device hardware',
                                        'Private - biometrics never leave your device',
                                        'Works offline on mobile apps'
                                    ].map((benefit, i) => (
                                        <div key={i} className="flex items-center gap-2">
                                            <Check size={16} className="text-emerald-500 flex-shrink-0" />
                                            <span className={`text-xs ${
                                                isDarkMode ? 'text-slate-300' : 'text-slate-700'
                                            }`}>
                                                {benefit}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Registered Devices */}
                            {credentials.length > 0 && (
                                <div>
                                    <h3 className={`text-sm font-bold mb-3 ${
                                        isDarkMode ? 'text-white' : 'text-slate-900'
                                    }`}>
                                        Registered Devices
                                    </h3>
                                    <div className="space-y-2">
                                        {credentials.map((cred) => (
                                            <div 
                                                key={cred.id}
                                                className={`flex items-center justify-between p-3 rounded-lg border ${
                                                    isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'
                                                }`}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center">
                                                        <Smartphone size={18} className="text-white" />
                                                    </div>
                                                    <div>
                                                        <p className={`text-sm font-semibold ${
                                                            isDarkMode ? 'text-white' : 'text-slate-900'
                                                        }`}>
                                                            {cred.deviceName}
                                                        </p>
                                                        <p className={`text-xs ${
                                                            isDarkMode ? 'text-slate-400' : 'text-slate-500'
                                                        }`}>
                                                            Added {new Date(cred.createdAt).toLocaleDateString()}
                                                        </p>
                                                    </div>
                                                </div>
                                                <button
                                                    onClick={() => handleRemove(cred.id)}
                                                    className={`p-2 rounded-lg transition-colors ${
                                                        isDarkMode 
                                                            ? 'hover:bg-red-900/50 text-red-400' 
                                                            : 'hover:bg-red-50 text-red-600'
                                                    }`}
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Error message with retry option */}
                            {lastError && (
                                <div className={`p-4 rounded-xl border ${
                                    isDarkMode ? 'bg-red-950/50 border-red-900/50' : 'bg-red-50 border-red-200'
                                }`}>
                                    <div className="flex items-start gap-3">
                                        <AlertCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
                                        <div className="flex-1">
                                            <p className={`text-sm font-semibold mb-1 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
                                                Setup Failed
                                            </p>
                                            <p className={`text-xs ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>
                                                {lastError}
                                            </p>
                                            {retryCount < 3 && (
                                                <button
                                                    onClick={() => handleSetup(true)}
                                                    disabled={isRegistering}
                                                    className="mt-2 text-xs font-semibold text-red-500 hover:text-red-400 transition-colors"
                                                >
                                                    ← Try Again
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Setup Button */}
                            <button
                                onClick={() => handleSetup()}
                                disabled={isRegistering}
                                className={`
                                    w-full py-4 rounded-xl font-bold text-base
                                    transition-all duration-300 ease-out
                                    flex items-center justify-center gap-2.5
                                    shadow-lg hover:shadow-xl
                                    disabled:opacity-50 disabled:cursor-not-allowed
                                    bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white active:scale-[0.98]
                                `}
                            >
                                {isRegistering ? (
                                    <>
                                        <Loader2 size={20} className="animate-spin" />
                                        Setting up...
                                    </>
                                ) : credentials.length > 0 ? (
                                    <>
                                        <Fingerprint size={20} />
                                        Add Another Device
                                    </>
                                ) : (
                                    <>
                                        <Shield size={20} />
                                        Enable Biometric Login
                                    </>
                                )}
                            </button>

                            {/* Info */}
                            <div className={`p-3 rounded-lg ${
                                isDarkMode ? 'bg-slate-800' : 'bg-slate-50'
                            }`}>
                                <p className={`text-xs leading-relaxed ${
                                    isDarkMode ? 'text-slate-400' : 'text-slate-600'
                                }`}>
                                    <strong>Privacy:</strong> Your biometric data never leaves your device. 
                                    We only store a cryptographic key that works with your device's secure enclave.
                                </p>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BiometricSetupModal;
